import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

const API = process.env.REACT_APP_API_BASE_URL;

interface AttemptDTO {
  id: number;
  classTestId: number;
  classTestTitle: string;
  level: string;
  chapterRange: string;
  status: string;
  submittedAt: string | null;
  lesenScore: number | null;
  lesenMaxScore: number | null;
  horenScore: number | null;
  horenMaxScore: number | null;
  schreibenScore: number | null;
  schreibenMaxScore: number | null;
  sprechenScore: number | null;
  sprechenMaxScore: number | null;
  totalScore: number | null;
  maxScore: number | null;
  percentage: number | null;
  passed: boolean | null;
  resultsPublished: boolean;
  overallFeedback: string | null;
}

interface Question {
  id: number;
  module: string;
  questionNumber: number;
  questionType: string;
  questionText: string;
  instructions: string | null;
  contextText: string | null;
  contextImageUrl: string | null;
  blankCount: number | null;
  points: number;
}

interface Answer {
  id: number;
  questionId: number;
  studentAnswer: string | null;
  isCorrect: boolean | null;
  pointsEarned: number | null;
  maxPoints: number | null;
  feedback: string | null;
  gradedBy: string | null;
}

const parseJsonArray = (s: string | null): string[] => {
  if (!s) return [];
  try {
    const p = JSON.parse(s);
    if (Array.isArray(p)) return p.map(String);
  } catch {}
  return [s];
};

const MODULE_LABEL: Record<string, string> = {
  LESEN: "📖 Lesen",
  HOREN: "🎧 Hören",
  SCHREIBEN: "✏️ Schreiben",
};

interface Props {
  attemptId: number;
  onBack: () => void;
}

const StudentClassTestResult: React.FC<Props> = ({ attemptId, onBack }) => {
  const [attempt, setAttempt] = useState<AttemptDTO | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const attemptRes = await fetch(
          `${API}/api/student/class-tests/attempts/${attemptId}`,
          { credentials: "include" }
        );
        if (!attemptRes.ok) return;
        const att: AttemptDTO = await attemptRes.json();
        setAttempt(att);

        const [qRes, aRes] = await Promise.all([
          fetch(`${API}/api/student/class-tests/${att.classTestId}/questions`, {
            credentials: "include",
          }),
          fetch(`${API}/api/student/class-tests/attempts/${attemptId}/answers`, {
            credentials: "include",
          }),
        ]);
        if (qRes.ok) setQuestions(await qRes.json());
        if (aRes.ok) setAnswers(await aRes.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [attemptId]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="text-center py-16 text-gray-400">Could not load results.</div>
    );
  }

  const answerMap = Object.fromEntries(answers.map((a) => [a.questionId, a]));

  const modules = ["LESEN", "HOREN", "SCHREIBEN"] as const;

  const scoreRows = [
    { label: "Lesen", score: attempt.lesenScore, max: attempt.lesenMaxScore },
    { label: "Hören", score: attempt.horenScore, max: attempt.horenMaxScore },
    { label: "Schreiben", score: attempt.schreibenScore, max: attempt.schreibenMaxScore },
    { label: "Sprechen", score: attempt.sprechenScore, max: attempt.sprechenMaxScore },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Back */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FaArrowLeft size={12} /> Back to My Tests
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs text-gray-500 mb-1">
                {attempt.level} · {attempt.chapterRange?.replace(/_/g, " ")}
              </div>
              <div className="font-bold text-gray-900 text-lg">{attempt.classTestTitle}</div>
            </div>
            {attempt.passed != null && (
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-lg ${
                  attempt.passed
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {attempt.passed ? (
                  <FaCheckCircle size={18} />
                ) : (
                  <FaTimesCircle size={18} />
                )}
                {attempt.percentage != null ? `${Math.round(attempt.percentage)}%` : "—"}{" "}
                {attempt.passed ? "Pass" : "Fail"}
              </div>
            )}
          </div>

          {/* Score breakdown */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {scoreRows.map(({ label, score, max }) => (
              <div key={label} className="bg-gray-50 rounded-lg px-3 py-2 text-center border border-gray-100">
                <div className="text-xs text-gray-500 mb-0.5">{label}</div>
                <div className="font-bold text-gray-900">
                  {score ?? "—"}/{max ?? "—"}
                </div>
              </div>
            ))}
          </div>

          {attempt.overallFeedback && (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-800">
              <span className="font-semibold">Feedback: </span>
              {attempt.overallFeedback}
            </div>
          )}
        </div>

        {/* Answers by module */}
        {answers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-400">
            Detailed answer review not available yet.
          </div>
        ) : (
          modules.map((mod) => {
            const modQuestions = questions
              .filter((q) => q.module === mod)
              .sort((a, b) => a.questionNumber - b.questionNumber);
            if (!modQuestions.length) return null;
            return (
              <div key={mod} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">
                  {MODULE_LABEL[mod]}
                </div>
                <div className="divide-y divide-gray-100">
                  {modQuestions.map((q) => {
                    const ans = answerMap[q.id];
                    const blankCount = q.blankCount && q.blankCount > 1 ? q.blankCount : null;
                    const studentParts = parseJsonArray(ans?.studentAnswer ?? null);

                    return (
                      <div key={q.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-400 mb-1">
                              Q{q.questionNumber} · {q.questionType.replace(/_/g, " ")}
                            </div>
                            {q.instructions && (
                              <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mb-2 border border-blue-100">
                                {q.instructions}
                              </div>
                            )}
                            {q.contextText && (
                              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-2 border border-gray-100 line-clamp-3">
                                {q.contextText}
                              </div>
                            )}
                            {q.contextImageUrl && (
                              <img
                                src={q.contextImageUrl}
                                alt="Context"
                                className="max-w-xs rounded-lg border border-gray-200 mb-2"
                              />
                            )}
                            <div className="text-sm font-medium text-gray-900 mb-2">
                              {q.questionText}
                            </div>

                            {/* Multi sub-answer display */}
                            {blankCount ? (
                              <div className="space-y-1">
                                {Array.from({ length: blankCount }).map((_, idx) => {
                                  const sa = studentParts[idx] ?? null;
                                  return (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                      <span className="text-xs text-gray-400 w-6 shrink-0">
                                        Q{q.questionNumber + idx}
                                      </span>
                                      <span
                                        className={`px-2 py-0.5 rounded ${
                                          sa
                                            ? "bg-blue-50 text-blue-800"
                                            : "bg-gray-50 text-gray-400 italic"
                                        }`}
                                      >
                                        {sa || "no answer"}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div
                                className={`text-sm px-3 py-2 rounded-lg border ${
                                  ans?.studentAnswer
                                    ? "bg-blue-50 border-blue-100 text-gray-800"
                                    : "bg-gray-50 border-gray-100 text-gray-400 italic"
                                }`}
                              >
                                {ans?.studentAnswer || "No answer submitted"}
                              </div>
                            )}

                            {ans?.feedback && (
                              <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1">
                                Feedback: {ans.feedback}
                              </div>
                            )}
                          </div>

                          {/* Points */}
                          <div className="shrink-0 text-center">
                            {ans ? (() => {
                              const isManualType = ["TEXT_INPUT","ESSAY","EMAIL","LETTER"].includes(q.questionType);
                              const pts = ans.pointsEarned ?? 0;
                              const max = ans.maxPoints ?? q.points;
                              const graded = ans.gradedBy != null;
                              let cls = "bg-gray-100 text-gray-600";
                              let icon = null;
                              if (isManualType) {
                                if (!graded) {
                                  cls = "bg-gray-100 text-gray-500";
                                } else if (pts === 0) {
                                  cls = "bg-red-100 text-red-700";
                                } else if (pts >= max) {
                                  cls = "bg-green-100 text-green-700";
                                  icon = <FaCheck size={11} />;
                                } else {
                                  cls = "bg-amber-100 text-amber-700";
                                }
                              } else {
                                if (ans.isCorrect === true) { cls = "bg-green-100 text-green-700"; icon = <FaCheck size={11} />; }
                                else if (ans.isCorrect === false) { cls = "bg-red-100 text-red-700"; icon = <FaTimes size={11} />; }
                              }
                              return (
                                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${cls}`}>
                                  {icon}
                                  {pts}/{max} pts
                                </div>
                              );
                            })() : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StudentClassTestResult;
